import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, telHref } from "@/lib/api";
import { useJourney, useSite } from "@/lib/site";
import { Field, PlateInput } from "./Form";
import { EstimateCard, VehicleSummary } from "./EstimateCard";
import { ReviewOutcome } from "./Outcomes";
import { ConditionSlider } from "./ConditionSlider";

const MESSAGES = {
  not_found: "We couldn't value that registration online. Please check it's correct – or send us your details and we'll review your car personally.",
  invalid_registration: "That registration wasn't recognised. Please check it and try again, or send us your details instead.",
  busy: "You've requested several valuations in a short time. Please try again later, or send us your details now.",
  network: "We couldn't connect just now. Please check your connection and try again.",
  no_estimate: "We've identified your car, but we can't give a reliable instant valuation for it. Send us your details and we'll review it personally.",
};
const GENERIC = "Instant valuations are temporarily unavailable. You can still send us your details and we'll review your car personally.";

const parseMiles = (s) => (s === "" ? NaN : Number(String(s).replace(/[,\s]/g, "")));

export const ValuationPanel = () => {
  const { cfg } = useSite();
  const j = useJourney();
  const nav = useNavigate();
  const prev = j.state.submitted ? null : j.state.valuation || j.state.manual;
  const [reg, setReg] = useState(prev?.registration || "");
  const [mi, setMi] = useState(prev?.mileage != null ? String(prev.mileage) : "");
  const [cond, setCond] = useState(prev?.condition || 5);
  const [hp, setHp] = useState("");
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState({});
  const [outcome, setOutcome] = useState(!j.state.submitted && j.state.valuation?.status === "ok" ? j.state.valuation : null);
  const available = cfg.valuation.available;

  const validate = () => {
    const e = {};
    const compact = reg.replace(/[^A-Za-z0-9]/g, "");
    if (compact.length < 2 || compact.length > 8) e.registration = "Please check the registration and try again.";
    const m = parseMiles(mi);
    if (!Number.isInteger(m) || m < 0 || m > 999999) e.mileage = "Please enter the mileage in miles, for example 45000.";
    setErrors(e);
    return Object.keys(e).length === 0 ? m : null;
  };

  const goManual = (lookup) => {
    const m = parseMiles(mi);
    j.reset({ manual: { registration: lookup?.registration || reg, mileage: lookup?.mileage ?? m, vehicle: lookup?.vehicle || null },
              valuation: lookup?.valuation_id ? lookup : null,
              intent: lookup?.status === "not_buying" ? "part_exchange" : sessionStorage.getItem("hcs_intent") || null });
    nav("/sell");
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (busy) return;
    const m = validate();
    if (m === null) return;
    if (!available) return goManual();
    setBusy(true);
    setOutcome(null);
    try {
      const { data } = await api.post("/public/valuation", { registration: reg, mileage: m, condition: cond, website: hp || null });
      if (data.status === "invalid") setErrors({ [data.field]: data.message });
      else {
        setOutcome(data);
        if (data.status === "ok") j.reset({ valuation: data });
      }
    } catch {
      setOutcome({ status: "unavailable", reason: "network" });
    } finally {
      setBusy(false);
    }
  };

  if (outcome?.status === "ok")
    return <EstimateCard v={outcome} onContinue={() => nav("/sell")} onChange={() => setOutcome(null)} />;

  return (
    <div className="space-y-5">
      <form onSubmit={submit} noValidate className="space-y-4" data-testid="valuation-form" aria-busy={busy}>
        <Field label="Your registration" htmlFor="reg" error={errors.registration}>
          <PlateInput id="reg" value={reg} onChange={setReg} error={errors.registration} />
        </Field>
        <Field label="Current mileage" htmlFor="mileage" error={errors.mileage}>
          <div className="relative">
            <input id="mileage" className={`field-input pr-16 text-lg ${errors.mileage ? "field-error" : ""}`} inputMode="numeric" pattern="[0-9,]*"
                   autoComplete="off" placeholder="e.g. 45000" value={mi} onChange={(e) => setMi(e.target.value.replace(/[^\d,]/g, ""))}
                   aria-invalid={!!errors.mileage} data-testid="mileage-input" enterKeyHint="go" />
            <span className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-sm text-slate-500">miles</span>
          </div>
        </Field>
        <ConditionSlider value={cond} onChange={setCond} />
        <input type="text" tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" name="website" value={hp} onChange={(e) => setHp(e.target.value)} />
        <Button type="submit" size="lg" className="btn-cta h-12 w-full text-base" disabled={busy} data-testid="get-estimate-button">
          {busy ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Checking your car…</> : "Get My Valuation"}
        </Button>
        {!available && <p className="text-sm text-slate-600" data-testid="valuation-unavailable-note">Instant valuations aren't available at the moment. Enter your registration and mileage to send us your details instead.</p>}
      </form>
      {outcome && (["manual_review", "not_buying"].includes(outcome.status)
        ? <ReviewOutcome outcome={outcome} onManual={() => goManual(outcome)} />
        : <Unavailable outcome={outcome} onManual={() => goManual(outcome.status === "no_estimate" ? outcome : null)} />)}
    </div>
  );
};

const Unavailable = ({ outcome, onManual }) => {
  const { cfg } = useSite();
  const msg = outcome.status === "no_estimate" ? MESSAGES.no_estimate : MESSAGES[outcome.reason] || GENERIC;
  return (
    <div className="fade-in space-y-4 rounded-md border border-slate-300 bg-slate-50 p-4" role="status" data-testid="valuation-unavailable">
      <p className="text-sm text-slate-800">{msg}</p>
      {outcome.status === "no_estimate" && <VehicleSummary v={outcome} />}
      {outcome.detail && <p className="text-xs text-amber-800" data-testid="valuation-tester-detail">Tester detail: {outcome.detail}</p>}
      <div className="flex flex-col gap-2 sm:flex-row">
        <Button onClick={onManual} className="btn-cta" data-testid="manual-enquiry-button">Send My Enquiry</Button>
        <Button variant="outline" asChild><a href={telHref(cfg.business.phone)} data-testid="unavailable-call-button"><Phone className="mr-1 h-4 w-4" /> Call {cfg.business.phone}</a></Button>
      </div>
    </div>
  );
};
